/**
 * tokenStore.js — pure latest-wins token reducer helpers.
 *
 * The CMS Self Signup backend rotates its authorization token on nearly every
 * call. This module owns the single rule for how the stored token evolves:
 * "latest-wins". Given the current token and a normalized response, the stored
 * token is replaced only when the response carries a non-null token; otherwise
 * it is left unchanged (Req 3.3, 3.4, 3.9).
 *
 * These helpers are intentionally pure (no I/O, no module-level mutable state)
 * so they can be composed into the Redux wizard slice and tested in isolation.
 */

/**
 * Determine whether a normalized response carries a usable rotating token.
 *
 * A token is considered present only when it is a non-empty string. `null`,
 * `undefined`, empty strings, and non-string values are all treated as "no
 * token" so that a malformed response never overwrites a valid stored token.
 *
 * @param {import('../api/normalizeResponse').NormalizedResponse | { token?: unknown } | null | undefined} response
 * @returns {boolean} true when the response carries a non-null, non-empty token
 */
export function hasToken(response) {
    if (response == null) {
        return false;
    }
    const { token } = response;
    return typeof token === 'string' && token.length > 0;
}

/**
 * Pure latest-wins token reducer.
 *
 * Returns the token that should be stored after processing a single normalized
 * response:
 * - When the response carries a non-null token, return that token (replace).
 * - Otherwise, return the current token unchanged (retain).
 *
 * @param {string|null} currentToken the currently stored rotating token
 * @param {import('../api/normalizeResponse').NormalizedResponse | { token?: unknown } | null | undefined} response
 *   a normalized response that may or may not carry a new token
 * @returns {string|null} the token to store next
 */
export function reduceToken(currentToken, response) {
    return hasToken(response) ? response.token : currentToken;
}

/**
 * Fold a sequence of normalized responses into the final stored token.
 *
 * Applying `reduceToken` left-to-right, the result equals the last non-null
 * token in the sequence, or the initial token when no response carried one
 * (Property 2: Token Latest-Wins Monotonicity).
 *
 * @param {string|null} initialToken the token stored before the sequence
 * @param {Array<import('../api/normalizeResponse').NormalizedResponse | { token?: unknown } | null | undefined>} responses
 * @returns {string|null} the token to store after the whole sequence
 */
export function reduceTokenSequence(initialToken, responses) {
    if (!Array.isArray(responses)) {
        return initialToken;
    }
    return responses.reduce((token, response) => reduceToken(token, response), initialToken);
}
